import { 
  getDocs, 
  getDoc, 
  setDoc, 
  query, 
  orderBy, 
  where,
  Timestamp
} from 'firebase/firestore';
import type { Frame, FrameFamily, PieceFrameSelection, UserFramePreferences } from '@shared/types/firestore';
import {
  getFrameDoc,
  getFrameFamiliesCollection,
  getFrameFamilyDoc,
  getFramesCollection,
  getUserFramePreferencesDoc
} from '../lib/collections';

/**
 * Service for managing frame families, frames, mat options, and user frame preferences
 * Works with both Unity standalone and Unity WebGL builds
 */
class FrameService {
  /**
   * Load all available frame families for a gallery
   */
  async loadFrameFamilies(galleryId: string): Promise<FrameFamily[]> {
    try {
      const familiesRef = getFrameFamiliesCollection(galleryId);
      const q = query(
        familiesRef,
        where('active', '==', true),
        orderBy('sortOrder', 'asc'),
        orderBy('name', 'asc')
      );
      
      const snapshot = await getDocs(q);
      const families = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as FrameFamily[];

      return families;
    } catch (error) {
      console.error('[Frame] Failed to load frame families:', error);
      throw error;
    }
  }

  /**
   * Get a specific frame family by ID
   */
  async getFrameFamily(galleryId: string, familyId: string): Promise<FrameFamily | null> {
    try {
      const familyRef = getFrameFamilyDoc(galleryId, familyId);
      const familyDoc = await getDoc(familyRef);
      
      if (!familyDoc.exists()) {
        console.warn(`[Frame] Frame family ${familyId} not found`);
        return null;
      }
      
      return {
        id: familyDoc.id,
        ...familyDoc.data()
      } as FrameFamily;
    } catch (error) {
      console.error(`[Frame] Failed to get frame family ${familyId}:`, error);
      throw error;
    }
  }

  /**
   * Load frames within a specific family
   */
  async loadFramesInFamily(galleryId: string, familyId: string): Promise<Frame[]> {
    try {
      const framesRef = getFramesCollection(galleryId, familyId);
      const q = query(
        framesRef,
        where('active', '==', true),
        orderBy('sortOrder', 'asc'),
        orderBy('name', 'asc')
      );
      
      const snapshot = await getDocs(q);
      const frames = snapshot.docs.map(doc => ({
        id: doc.id,
        familyId, // Add the familyId to the frame data
        ...doc.data()
      })) as Frame[];

      return frames;
    } catch (error) {
      console.error(`[Frame] Failed to load frames for family ${familyId}:`, error);
      throw error;
    }
  }

  /**
   * Load all frames across all families for a gallery
   */
  async loadAllFrames(galleryId: string): Promise<Frame[]> {
    try {
      const families = await this.loadFrameFamilies(galleryId);
      const framesByFamily = await Promise.all(
        families.flatMap(family =>
          family.id ? [this.loadFramesInFamily(galleryId, family.id)] : []
        )
      );

      return framesByFamily.flat();
    } catch (error) {
      console.error('[Frame] Failed to load all frames:', error);
      throw error;
    }
  }

  /**
   * Get a specific frame by ID (requires familyId)
   */
  async getFrame(galleryId: string, familyId: string, frameId: string): Promise<Frame | null> {
    try {
      const frameRef = getFrameDoc(galleryId, familyId, frameId);
      const frameDoc = await getDoc(frameRef);
      
      if (!frameDoc.exists()) {
        console.warn(`[Frame] Frame ${frameId} not found in family ${familyId}`);
        return null;
      }
      
      return {
        id: frameDoc.id,
        familyId,
        ...frameDoc.data()
      } as Frame;
    } catch (error) {
      console.error(`[Frame] Failed to get frame ${frameId}:`, error);
      throw error;
    }
  }

  /**
   * Find a frame by ID across all families (slower but more convenient)
   */
  async findFrameById(galleryId: string, frameId: string): Promise<Frame | null> {
    try {
      const families = await this.loadFrameFamilies(galleryId);

      for (const family of families) {
        if (!family.id) continue;

        const frame = await this.getFrame(galleryId, family.id, frameId);
        if (frame?.active) {
          return frame;
        }
      }

      return null;
    } catch (error) {
      console.error(`[Frame] Failed to find frame ${frameId}:`, error);
      return null;
    }
  }

  /**
   * Load user frame preferences
   */
  async loadUserFramePreferences(galleryId: string, userId: string): Promise<UserFramePreferences | null> {
    try {
      const prefsRef = getUserFramePreferencesDoc(galleryId, userId);
      const prefsDoc = await getDoc(prefsRef);
      
      if (!prefsDoc.exists()) {
        return null;
      }
      
      return {
        userId: prefsDoc.id,
        ...prefsDoc.data()
      } as UserFramePreferences;
    } catch (error) {
      console.error(`[Frame] Failed to load preferences for user ${userId}:`, error);
      throw error;
    }
  }

  /**
   * Save user frame preferences
   */
  async saveUserFramePreferences(
    galleryId: string, 
    userId: string, 
    preferences: Partial<UserFramePreferences>
  ): Promise<void> {
    try {
      const prefsRef = getUserFramePreferencesDoc(galleryId, userId);
      const now = Timestamp.now();
      
      const prefsData = {
        ...preferences,
        userId,
        modifiedDateUTC: now
      };
      
      // If this is a new document, add createdDateUTC
      const existingDoc = await getDoc(prefsRef);
      if (!existingDoc.exists()) {
        prefsData.createdDateUTC = now;
        prefsData.pieceFrames = prefsData.pieceFrames || {};
      }
      
      await setDoc(prefsRef, prefsData, { merge: true });
    } catch (error) {
      console.error(`[Frame] Failed to save preferences for user ${userId}:`, error);
      throw error;
    }
  }

  /**
   * Apply frame selection to a specific piece
   */
  async applyFrameToPiece(
    galleryId: string,
    userId: string,
    pieceId: string,
    frameSelection: Omit<PieceFrameSelection, 'appliedAt'>
  ): Promise<void> {
    try {
      const now = Timestamp.now();
      
      // Load existing preferences or create new ones
      const existingPrefs = await this.loadUserFramePreferences(galleryId, userId);
      const pieceFrames = existingPrefs?.pieceFrames || {};
      
      // Update the specific piece frame selection
      pieceFrames[pieceId] = {
        ...frameSelection,
        appliedAt: now
      };
      
      await this.saveUserFramePreferences(galleryId, userId, {
        pieceFrames
      });
    } catch (error) {
      console.error(`[Frame] Failed to apply frame to piece ${pieceId}:`, error);
      throw error;
    }
  }

  /**
   * Get frame selection for a specific piece
   */
  async getPieceFrameSelection(
    galleryId: string,
    userId: string,
    pieceId: string
  ): Promise<PieceFrameSelection | null> {
    try {
      const preferences = await this.loadUserFramePreferences(galleryId, userId);
      return preferences?.pieceFrames?.[pieceId] || null;
    } catch (error) {
      console.error(`[Frame] Failed to get frame selection for piece ${pieceId}:`, error);
      return null;
    }
  }

  /**
   * Remove frame from a piece (reset to default)
   */
  async removeFrameFromPiece(
    galleryId: string,
    userId: string,
    pieceId: string
  ): Promise<void> {
    try {
      const preferences = await this.loadUserFramePreferences(galleryId, userId);
      if (!preferences?.pieceFrames?.[pieceId]) {
        return;
      }
      
      // Remove the piece from the pieceFrames object
      const { [pieceId]: _removed, ...remainingFrames } = preferences.pieceFrames;
      
      await this.saveUserFramePreferences(galleryId, userId, {
        pieceFrames: remainingFrames
      });
    } catch (error) {
      console.error(`[Frame] Failed to remove frame from piece ${pieceId}:`, error);
      throw error;
    }
  }

  /**
   * Get recommended frames for a piece
   */
  getRecommendedFrames(piece: { recommendedFrames?: string[]; defaultFrameId?: string }): string[] {
    const recommended: string[] = [];
    
    // Add default frame if specified
    if (piece.defaultFrameId) {
      recommended.push(piece.defaultFrameId);
    }
    
    // Add other recommended frames
    if (piece.recommendedFrames) {
      piece.recommendedFrames.forEach(frameId => {
        if (!recommended.includes(frameId)) {
          recommended.push(frameId);
        }
      });
    }
    
    return recommended;
  }
}

// Export singleton instance
export const frameService = new FrameService();
export default frameService;
